import { useState, useCallback } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/**
 * Custom hook for lazy loading users
 * Only fetches users when explicitly requested
 */
export const useLazyUsers = (token) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const fetchUsers = useCallback(async () => {
    // If no token, return empty array
    if (!token) {
      return [];
    }

    // If already loaded, return cached data
    if (loaded && users.length > 0) {
      return users;
    }

    // If already loading, don't make another request
    if (loading) {
      return users;
    }

    setLoading(true);
    setError(null);

    try {
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Fetch all users by setting a very high limit
      const response = await axios.get(`${API_BASE}/users?limit=1000`, {
        headers
      });
      
      // Handle different response structures
      let userData = [];
      if (response.data) {
        if (response.data.data && Array.isArray(response.data.data)) {
          userData = response.data.data;
        } else if (response.data.users && Array.isArray(response.data.users)) {
          userData = response.data.users;
        } else if (Array.isArray(response.data)) {
          userData = response.data;
        }
      }
      
      // Filter out invalid user entries and ensure required fields
      const validUsers = userData.filter(user => 
        user && 
        user.id && 
        (user.username || user.full_name || user.email)
      );
      
      setUsers(validUsers);
      setLoaded(true);
      return validUsers;
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.message || 'Failed to fetch users');
      setUsers([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [token, loaded, users.length, loading]);

  const refreshUsers = useCallback(async () => {
    setLoaded(false);
    return await fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    loaded,
    error,
    fetchUsers: token ? fetchUsers : () => Promise.resolve([]),
    refreshUsers: token ? refreshUsers : () => Promise.resolve([])
  };
}; 